import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { copy } from '../../config/copy';
import { usePlayer } from '../../stores/player';
import { useSession } from '../../stores/session';
import { favorites, playlists, type Playlist } from '../library/api';
import type { TrackSummary } from '../tracks/TrackList';
import { Artwork } from './Artwork';

/**
 * Favourites and playlists, down the left of the dashboard.
 *
 * ## Why this exists when the dock already has a Library
 *
 * They answer the same question at different moments. The dock's block is a
 * launcher - reachable from any page, opened deliberately, closed again. This is
 * a surface: it is simply there while somebody is deciding what to listen to,
 * which is the entire purpose of the page it sits on.
 *
 * Deliberately a decision rather than a duplication that crept in. The other
 * option was for the library to become the app's left rail on every page, the
 * way Spotify does it, and for the dock's block to retire into it - which is
 * cleaner and was not what was wanted here.
 *
 * ## The same queries as the dock
 *
 * `['favorites']` and `['playlists', username]`, so a track favourited in one
 * appears in the other without a refresh, and neither can show a list the other
 * disagrees with.
 *
 * ## Not on a phone
 *
 * The rails are hidden below `xl` and the middle column becomes the page. Three
 * columns on a narrow screen is one column of content and two of noise, and the
 * dock's Library block is still there for anybody who wants the lists.
 */
export function LibraryRail() {
  const me = useSession((state) => state.user);

  const { data: liked } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => favorites(),
    enabled: Boolean(me),
  });

  const { data: lists } = useQuery({
    queryKey: ['playlists', me?.username],
    queryFn: () => playlists.mine(me!.username),
    enabled: Boolean(me?.username),
  });

  if (!me) return null;

  const tracks = liked?.items ?? [];
  const collections = lists?.items ?? [];

  return (
    <aside className="flex flex-col gap-8" data-library-rail>
      <section aria-labelledby="rail-favorites">
        <div className="mb-3 flex items-baseline justify-between gap-3 border-b border-line pb-2">
          <h2 id="rail-favorites" className="meta-caps text-faint">
            {copy['noun.like.plural']}
          </h2>
          <Link to="/favorites" className="font-mono text-[10px] text-soft hover:text-ink">
            all →
          </Link>
        </div>

        {tracks.length === 0 ? (
          <p className="text-xs text-faint">
            Nothing saved yet. The heart on any track puts it here.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {tracks.slice(0, 6).map((track, index) => (
              <Row key={track.id} track={track} queue={tracks.slice(0, 6)} index={index} />
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="rail-playlists">
        <div className="mb-3 flex items-baseline justify-between gap-3 border-b border-line pb-2">
          <h2 id="rail-playlists" className="meta-caps text-faint">
            Playlists
          </h2>
        </div>

        {collections.length === 0 ? (
          <p className="text-xs text-faint">
            No playlists yet. The plus on the player bar starts one.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {collections.slice(0, 8).map((list: Playlist) => (
              <li key={list.id}>
                <Link
                  to={`/playlists/${list.id}`}
                  className="flex items-baseline justify-between gap-2 rounded-[var(--radius-control)] px-1.5 py-1.5 transition-colors hover:bg-raised"
                >
                  <span className="min-w-0 truncate text-[13px] text-ink">{list.title}</span>
                  <span className="shrink-0 font-mono text-[10px] text-faint">
                    {list.track_count}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </aside>
  );
}

/**
 * One favourite: artwork that plays, and a title that navigates.
 *
 * ## Two controls, not one
 *
 * The whole row was a link. It cannot stay one, because a button inside an
 * anchor is invalid markup and a browser is entitled to do whatever it likes
 * with the click - so the artwork becomes the play button and the text keeps
 * the link. Which also happens to be the right division: the picture is the
 * thing people press to hear something, and the words are the thing they press
 * to go and look at it.
 *
 * ## The glyph stays for whatever is playing
 *
 * Hover reveals it, and the current track keeps it whether the pointer is there
 * or not. A rail that only ever shows its state on hover cannot show it at all
 * on a touch screen, and "which of these is playing" is worth answering without
 * one.
 *
 * ## Pressing one plays the rest
 *
 * The visible six become the queue, so a favourite is the start of a listen
 * rather than a single track that stops. Not the whole favourites list, because
 * what somebody can see is what they think they are starting.
 */
function Row({
  track,
  queue,
  index,
}: {
  track: TrackSummary;
  queue: TrackSummary[];
  index: number;
}) {
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const play = usePlayer((state) => state.play);
  const toggle = usePlayer((state) => state.toggle);

  const isCurrent = current?.id === track.id;
  const isPlaying = isCurrent && playing;

  return (
    <li className="group flex items-center gap-2.5 rounded-[var(--radius-control)] p-1.5 transition-colors hover:bg-raised">
      <button
        type="button"
        aria-label={isPlaying ? `Pause ${track.title}` : `Play ${track.title}`}
        data-rail-play={track.id}
        onClick={() =>
          isCurrent ? toggle() : void play(track, queue.slice(index), 'dashboard')
        }
        className="relative h-8 w-8 shrink-0 overflow-hidden rounded-[var(--radius-chip)]"
      >
        <Artwork
          track={{
            title: track.title,
            artwork: null,
            artwork_small: track.artwork_small ?? null,
          }}
          className="h-full w-full"
        />

        {/*
          `veil`, not `void`.

          A scrim exists to make a white glyph readable over artwork nobody
          chose, so it has to be dark in both themes - and `--color-void` is the
          page colour, which in the light theme is very nearly white. Copied
          from the grid below, it produced a white triangle on a white ground,
          perfectly present and completely invisible. `--color-veil` is the
          token that stays dark on purpose.
        */}
        <span
          aria-hidden
          className={`absolute inset-0 flex items-center justify-center bg-veil transition-opacity ${
            isPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          {isPlaying ? <PauseGlyph /> : <PlayGlyph />}
        </span>
      </button>

      <Link to={`/tracks/${track.id}`} className="min-w-0 flex-1">
        <span className={`block truncate text-[13px] ${isCurrent ? 'text-accent' : 'text-ink'}`}>
          {track.title}
        </span>
        <span className="block truncate font-mono text-[10px] text-faint">
          {track.owner?.display_name ?? track.owner?.username ?? ''}
        </span>
      </Link>
    </li>
  );
}

/* Smaller than the grid's, because the target here is 32px rather than 160. */
function PlayGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 translate-x-[1px] text-white" fill="currentColor" aria-hidden="true">
      <path d="M6 4.5v11l10-5.5z" />
    </svg>
  );
}

function PauseGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 text-white" fill="currentColor" aria-hidden="true">
      <path d="M6.5 4.5h2.5v11H6.5zM11 4.5h2.5v11H11z" />
    </svg>
  );
}
